/** Canonical production origin for absolute URLs (canonical, OG, Twitter). */
export const SITE_ORIGIN = 'https://www.nsave.com';

/** Stable deployed asset origin while the canonical domain still serves Webflow. */
export const WEBSITE_ASSET_ORIGIN = 'https://website.nsave.com';

/** Default social share image (absolute and directly crawlable). */
export const DEFAULT_OG_IMAGE = `${WEBSITE_ASSET_ORIGIN}/images/home/nsave-og.jpg`;

export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

/** Turn a site-relative path into an absolute `https://www.nsave.com/...` URL. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${SITE_ORIGIN}${path}`;
}

/** Resolve website assets independently from the canonical page origin. */
export function absoluteAssetUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${WEBSITE_ASSET_ORIGIN}${path}`;
}

type HeadMeta =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };

export interface JsonLdScript {
  type: 'application/ld+json';
  children: string;
}

/** Serialize trusted structured data without allowing a closing script tag. */
export function jsonLdScript(value: Record<string, unknown>): JsonLdScript {
  return {
    type: 'application/ld+json',
    children: JSON.stringify(value).replaceAll('<', '\\u003c'),
  };
}

export function siteJsonLdScripts(): JsonLdScript[] {
  return [
    jsonLdScript({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': ORGANIZATION_ID,
      name: 'nsave',
      url: SITE_ORIGIN,
      logo: absoluteUrl('/images/shared/nsave-logo.svg'),
    }),
    jsonLdScript({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      url: SITE_ORIGIN,
      name: 'nsave',
      publisher: { '@id': ORGANIZATION_ID },
      inLanguage: 'en',
    }),
  ];
}

export function webPageJsonLd(options: {
  title: string;
  description: string;
  canonical: string;
  image?: string;
}): JsonLdScript {
  return jsonLdScript({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${options.canonical}#webpage`,
    url: options.canonical,
    name: options.title,
    description: options.description,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORGANIZATION_ID },
    ...(options.image
      ? {
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: options.image,
          },
        }
      : {}),
    inLanguage: 'en',
  });
}

/**
 * Standard page head: title, description, absolute OG/Twitter image, canonical.
 */
export function pageHeadMeta(options: {
  title: string;
  description: string;
  /** Path only, e.g. `/accounts` or `/`. */
  path: string;
  /** Relative (`/images/...`) or absolute URL. Defaults to the site OG image. */
  ogImage?: string;
  /** Route-specific stylesheets that should not be merged into root critical CSS. */
  stylesheets?: string[];
  /** Small route-specific styles that should be emitted in the initial document. */
  inlineStyles?: string[];
}): {
  meta: HeadMeta[];
  links: Array<{ rel: string; href: string }>;
  styles: Array<{ children: string }>;
  scripts: JsonLdScript[];
} {
  const ogImage = absoluteAssetUrl(options.ogImage ?? DEFAULT_OG_IMAGE);
  const canonical = absoluteUrl(options.path === '/' ? '/' : options.path);
  const isDefaultOgImage = ogImage === DEFAULT_OG_IMAGE;

  return {
    meta: [
      { title: options.title },
      { name: 'description', content: options.description },
      { property: 'og:title', content: options.title },
      { property: 'og:description', content: options.description },
      { property: 'og:url', content: canonical },
      { property: 'og:site_name', content: 'nsave' },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:secure_url', content: ogImage },
      ...(isDefaultOgImage
        ? [
            { property: 'og:image:type', content: 'image/jpeg' },
            { property: 'og:image:width', content: '2434' },
            { property: 'og:image:height', content: '1278' },
            {
              property: 'og:image:alt',
              content: 'nsave — A global account for the rest of us',
            },
          ]
        : []),
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: options.title },
      { name: 'twitter:description', content: options.description },
      { name: 'twitter:image', content: ogImage },
      ...(isDefaultOgImage
        ? [
            {
              name: 'twitter:image:alt',
              content: 'nsave — A global account for the rest of us',
            },
          ]
        : []),
    ],
    links: [
      { rel: 'canonical', href: canonical },
      ...(options.stylesheets ?? []).map((href) => ({ rel: 'stylesheet', href })),
    ],
    styles: (options.inlineStyles ?? []).map((children) => ({ children })),
    scripts: [
      webPageJsonLd({
        title: options.title,
        description: options.description,
        canonical,
        image: ogImage,
      }),
    ],
  };
}
